import { customElement, property, query } from 'lit/decorators.js';
import {
  GzAutomaticActionSettingsWebComponent,
} from '../../automaticActionSettings/component/GzAutomaticActionSettingsWebComponent';
import { hasCreationProcessQueryRule, QuarantineSettings } from '../../../types';
import { DetectionRuleType, GridType } from '../../../../../../customRulesGrid/types';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { html, nothing, TemplateResult } from 'lit';
import { InfoBoxSeverity } from '../../../../../../../ui/gzInfoBox/types';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { CSSResultGroup } from '@lit/reactive-element/css-tag';
import { styles } from '../view/styles';
import { convertRuleFromFrontendToBackendFormat } from '../../../../../../customRulesGrid/utils';

@customElement('gz-quarantine-automatic-action-settings')
export class GzQuarantineAutomaticActionSettingsWebComponent
  extends GzAutomaticActionSettingsWebComponent<QuarantineSettings>
{
  @property({ type: Object, attribute: false, reflect: false })
  public rule: any;

  @property({ type: Boolean, attribute: false, reflect: false })
  public isGZ: boolean;

  @property({ type: String, attribute: false, reflect: false })
  public gridType: GridType;

  @property({ type: Boolean, attribute: false, reflect: false })
  public areAutomaticActionsAvailable: boolean;

  private readonly: boolean;

  @query('#include-parent-process')
  private includeParentProcessCheckbox: GzCheckboxWebComponent;

  @query('#include-children-processes')
  private includeChildrenProcessesCheckbox: GzCheckboxWebComponent;

  public static get styles(): CSSResultGroup {
    return styles;
  }

  public connectedCallback(): void {
    super.connectedCallback();

    this.readonly = DetectionRuleType.PROCESS !== this.rule.query?.type && !hasCreationProcessQueryRule(this.rule);
  }

  protected getTitle(): string {
    return translateFn('Edit quarantine configuration');
  }

  protected getDescription(): string {
    return translateFn('Quarantine the file or process that triggered the detection.');
  }

  protected renderContent(): TemplateResult {
    return html`
      <custom-rules-rule-outcome
        class="section mt-3"
        .rule=${convertRuleFromFrontendToBackendFormat(this.rule, this.gridType, this.areAutomaticActionsAvailable)}
        .isGZ=${this.isGZ}
        .gridType=${this.gridType}
        .displayDoTheFollowingSection=${false}
        .displayShowButtons=${true}
      >
      </custom-rules-rule-outcome>

      ${
        this.readonly
          ? html`
            <gz-info-box class="mb-2" severity="${InfoBoxSeverity.INFORMATIVE}">
              ${translateFn('Behavior description: If either of the two conditions is not met, no editing action can be taken to quarantine the item.')}
            </gz-info-box>
          `
          : nothing
      }

      <gz-title
        class="section-title text-uppercase mb-2${this.readonly ? ' disabled' : ''}"
        fontweight="${FontWeights.SEMIBOLD}"
        fontsize="${FontSizes.PARAGRAPH_11}"
      >
        ${translateFn('Action configuration')}
      </gz-title>

      <gz-labeled-content
        class="mt-3${this.readonly ? ' disabled' : ''}"
        label=${translateFn('Application')}
        labelFontSize="${FontSizes.PARAGRAPH_13}"
        hasColon
      >
        <gz-checkbox
          id="include-parent-process"
          label="${translateFn('Include parent process file')}"
          ?readonly="${this.readonly}"
          ?checked="${this.settings.includeParentProcess}"
        >
        </gz-checkbox>
        <gz-checkbox
          id="include-children-processes"
          class="mt-2"
          label="${translateFn('Include files of child processes')}"
          ?readonly="${this.readonly}"
          ?checked="${this.settings.includeChildrenProcesses}"
        >
        </gz-checkbox>
      </gz-labeled-content>
    `;
  }

  protected editAction(): void {
    this.settings.includeParentProcess = this.includeParentProcessCheckbox.getFormValue();
    this.settings.includeChildrenProcesses = this.includeChildrenProcessesCheckbox.getFormValue();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-quarantine-automatic-action-settings': GzQuarantineAutomaticActionSettingsWebComponent;
  }
}
